import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import { ArrowUpRight, Check, Bell, Smartphone, Clock, Palette, Target, Lightbulb, Heart, Globe, ChevronDown, ChevronUp, Zap, TrendingUp, Users } from 'lucide-react';
import { useState } from 'react';

const features = [
  { icon: Globe, title: 'Browser push', text: 'Send notifications to Chrome, Firefox, Safari, Opera, Brave, and Opera Mini without email.' },
  { icon: Smartphone, title: 'Mobile push', text: 'Reach users on iOS and Android apps with native push notifications.' },
  { icon: Clock, title: 'Scheduled campaigns', text: 'Schedule notifications in advance for optimal delivery times.' },
  { icon: Palette, title: 'Rich media attachments', text: 'Include images, videos, and interactive content in notifications.' },
  { icon: Target, title: 'Smart segmentation', text: 'Segment users by behavior, location, device, and custom attributes.' },
  { icon: Zap, title: 'Real-time delivery', text: 'Messages arrive instantly with sub-second delivery times.' },
];

const useCases = [
  { title: 'Cart abandonment', text: 'Send reminders when customers leave items in their cart with time-sensitive offers.' },
  { title: 'Order updates', text: 'Notify customers about shipping, delivery, and order status with rich tracking info.' },
  { title: 'Promotional campaigns', text: 'Announce sales, discounts, and new product launches with eye-catching media.' },
  { title: 'Service alerts', text: 'Inform users about maintenance, downtime, or urgent updates in real-time.' },
  { title: 'Content publishing', text: 'Notify readers when new articles, videos, or content is published.' },
  { title: 'Event reminders', text: 'Send automated reminders for webinars, meetings, and upcoming events.' },
];

const benefits = [
  { icon: TrendingUp, title: '4x higher CTR', description: 'Push notifications achieve 4x higher click-through rates compared to email.' },
  { icon: Users, title: '+25% retention', description: 'Users who receive push notifications show 25% higher retention rates.' },
  { icon: Zap, title: '<1s delivery', description: 'Messages arrive instantly with sub-second delivery times.' },
  { icon: Bell, title: '6+ platforms', description: 'Support for Chrome, Firefox, Safari, Opera, Brave, Opera Mini, iOS, and Android.' },
];

const platformSupport = [
  { platform: 'Chrome', status: 'Supported', icon: Globe },
  { platform: 'Firefox', status: 'Supported', icon: Globe },
  { platform: 'Safari', status: 'Supported', icon: Globe },
  { platform: 'Opera', status: 'Supported', icon: Globe },
  { platform: 'Brave', status: 'Supported', icon: Globe },
  { platform: 'Opera Mini', status: 'Supported', icon: Globe },
  { platform: 'iOS', status: 'Supported', icon: Smartphone },
  { platform: 'Android', status: 'Supported', icon: Smartphone },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'Do users need to install an app?',
    answer: 'No. Browser push notifications work without app installation on desktop and mobile browsers including Chrome, Firefox, Safari, Opera, Brave, and Opera Mini. Mobile push requires a native app for iOS and Android.',
  },
  {
    question: 'Can users opt out?',
    answer: 'Yes. Users can disable push notifications at any time through their browser or device settings. We respect user privacy and make it easy to opt out.',
  },
  {
    question: 'What platforms are supported?',
    answer: 'We support Chrome, Firefox, Safari, Opera, Brave, Opera Mini for browsers, and iOS (APNS) and Android (FCM) for mobile apps. This gives you coverage across all major platforms.',
  },
  {
    question: 'How does scheduling work?',
    answer: 'You can schedule notifications by date, time, or recurring patterns. Delivery is time zone-aware and based on user behavior for optimal engagement. Schedule campaigns in advance and let them run automatically.',
  },
  {
    question: 'What is the pricing?',
    answer: 'Push Notifications is available as a $12/month add-on for unlimited sends. No per-message fees. Send as many notifications as you need across all platforms.',
  },
  {
    question: 'Is it included in all plans?',
    answer: 'Push Notifications is an optional add-on available on all plans for $12/month with unlimited sends. Add it to Free, Pro, or Enterprise plans to enable push notification capabilities.',
  },
  {
    question: 'What is smart prompting?',
    answer: 'Smart prompting is an AI-powered feature that recommends optimal send times, message content, and segmentation strategies based on historical performance data. It helps you craft more effective campaigns that drive higher engagement.',
  },
  {
    question: 'How does in-app personalization work?',
    answer: 'In-app personalization lets you trigger in-app messages, update content dynamically, and customize the user experience based on notification interactions and user preferences. Create personalized onboarding flows and context-aware experiences.',
  },
];

function FAQAccordion({ faqs }: { faqs: Array<{ question: string; answer: string }> }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="flex w-full items-center justify-between p-5 text-left"
            aria-expanded={openIndex === index}
          >
            <span className="pr-4 font-semibold text-slate-900">{faq.question}</span>
            {openIndex === index ? (
              <ChevronUp className="h-5 w-5 shrink-0 text-slate-500" />
            ) : (
              <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" />
            )}
          </button>
          {openIndex === index && (
            <div className="px-5 pb-5">
              <p className="text-slate-600">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function PushNotifications() {
  return (
    <>
      <SEO
        title="Push Notifications - Real-time Browser & Mobile Engagement | ZuriHub"
        description="Engage customers in real-time with browser and mobile push notifications. Send scheduled campaigns, rich media attachments, and segmented messages across Chrome, Firefox, Safari, Android, and iOS. $12/month for unlimited sends."
        path="/push-notifications"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <Bell className="h-4 w-4" /> $12/month add-on
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Reach customers <span className="gradient-text">instantly</span>, wherever they are
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Engage users in real-time with browser and mobile push notifications. Send scheduled campaigns, rich media attachments, and segmented messages across Chrome, Firefox, Safari, Android, and iOS from one unified dashboard.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="/pricing"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
                >
                  View Pricing <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative rounded-3xl border border-slate-700 bg-slate-900/60 p-2 backdrop-blur">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/A%20PUSH.png"
                  alt="Push Notifications"
                  className="rounded-2xl w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Powerful push notification features</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to engage customers in real-time.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="platforms-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="platforms-heading" className="section-title">Platform support</h2>
            <p className="section-subtitle mx-auto mt-4">Reach users across all major browsers and mobile platforms.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {platformSupport.map((platform) => (
              <div key={platform.platform} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                  <platform.icon className="h-6 w-6" />
                </div>
                <h3 className="font-bold text-slate-900">{platform.platform}</h3>
                <p className="mt-2 text-sm text-slate-600">{platform.status}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Why push notifications matter</h2>
            <p className="section-subtitle mx-auto mt-4">Benefits that transform your customer engagement.</p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <benefit.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{benefit.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="usecases-heading" className="text-center section-title">Use cases</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <h3 className="font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandMarquee />

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="how-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 id="how-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">How it works</h2>
              <p className="mt-4 text-slate-300">
                Send targeted, scheduled, and rich media push notifications to browsers and mobile applications from one unified dashboard.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  {
                    icon: Bell,
                    title: 'Request permission',
                    description: 'Your website or app prompts users to allow push notifications with native browser prompts.',
                  },
                  {
                    icon: Target,
                    title: 'Segment users',
                    description: 'Create segments based on behavior, location, device type, or custom attributes.',
                  },
                  {
                    icon: Palette,
                    title: 'Create campaigns',
                    description: 'Compose messages with rich media, deep links, and personalization.',
                  },
                  {
                    icon: Clock,
                    title: 'Schedule or send',
                    description: 'Send immediately or schedule for optimal delivery times based on user time zones.',
                  },
                  {
                    icon: Zap,
                    title: 'Track performance',
                    description: 'Monitor delivery, clicks, conversions, and engagement to optimize campaigns.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">{item.title}</h3>
                      <p className="mt-1 text-sm text-slate-400">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="font-bold">Advanced features</h3>
              <ul className="mt-4 space-y-3 text-slate-300">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Smart prompting AI recommendations</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> In-app personalization</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Behavior-based segmentation</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Location-based targeting</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Time zone-aware delivery</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> A/B testing capabilities</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about push notifications</p>
          </div>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>

      <section className="bg-gradient-to-br from-blue-600 to-blue-700 py-24 text-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Start engaging with push notifications</h2>
          <p className="text-xl text-blue-100 mb-8">Add Push Notifications for $12/month and send unlimited push campaigns across all platforms.</p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-blue-600 transition hover:bg-blue-50"
          >
            Get Started <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
